import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import IntervalInput, {
  parseIntervalInput,
  intervalToLatex,
} from "../../../../components/inputs/IntervalInput";
import {
  parseRootFractionInput,
  formatLatexPreview as formatNumLatex,
} from "../../../../components/inputs/RootFractionInput";
import FractionInput, {
  formatLatexPreview as formatFracPreview,
} from "../../../../components/inputs/FractionInput";

/* ===== helpers ===== */
const coeffX = (a) => (a === 1 ? "x" : a === -1 ? "-x" : `${a}x`);
const withSign = (n) => (n >= 0 ? `+ ${n}` : `- ${Math.abs(n)}`);
const toLatexNumber = (raw) => (formatNumLatex(String(raw), "") || String(raw));

const gcd = (a, b) => {
  a = Math.abs(a); b = Math.abs(b);
  while (b) { const t = a % b; a = b; b = t; }
  return a || 1;
};

function simplifyFrac(num, den) {
  if (den < 0) { num = -num; den = -den; }
  const g = gcd(num, den);
  return { num: num / g, den: den / g };
}

/** 9/1 -> "9", w przeciwnym razie \\dfrac{...}{...} (ze znakiem) */
function improperLatexFromFrac(num, den) {
  const { num: N, den: D } = simplifyFrac(num, den);
  if (N % D === 0) return String(N / D);
  const sign = N < 0 ? "-" : "";
  return `${sign}\\dfrac{${Math.abs(N)}}{${D}}`;
}

/** liczba mieszana jeśli ma sens; w p.p. null */
function mixedLatexFromFrac(num, den) {
  const { num: N, den: D } = simplifyFrac(num, den);
  if (N % D === 0) return null;
  const A = Math.floor(Math.abs(N) / D);
  const R = Math.abs(N) % D;
  if (A === 0) return null;
  const sign = N < 0 ? "-" : "";
  return `${sign}${A}\\;\\dfrac{${R}}{${D}}`;
}

/** preferuj mieszany; inaczej całkowita/„niewłaściwy” */
function finalPointLatex(num, den) {
  return mixedLatexFromFrac(num, den) || improperLatexFromFrac(num, den);
}

/* ===== generate ===== */
export function generateTask() {
  const pick = (min, max) =>
    (Math.random() < 0.5 ? -1 : 1) * (Math.floor(Math.random() * (max - min + 1)) + min);

  // losujemy parametry
  let c = 0; while (c === 0) c = pick(1, 6); // c ≠ 0
  let a = pick(1, 6);
  const b = pick(1, 10);
  const d = pick(1, 10);
  const k = Math.floor(Math.random() * 7) + 2; // 2..8

  // unikamy degeneracji: a - k c ≠ 0 (jedno rozwiązanie "kandydat")
  while (a - k * c === 0) a = pick(1, 6);

  const leftNum = `${coeffX(a)} ${withSign(b)}`;
  const leftDen = `${coeffX(c)} ${withSign(d)}`;
  const latex = `\\dfrac{${leftNum}}{${leftDen}} = ${toLatexNumber(k)}`;

  const x0 = simplifyFrac(-d, c);                   // wykluczony punkt
  const xr = simplifyFrac(k * d - b, a - k * c);    // kandydat rozwiązania

  // czy kandydat wpada w wykluczenie? (wtedy brak rozwiązań)
  const noSolution = x0.num * xr.den === xr.num * x0.den;

  return {
    type: "RationalSolveAndDomainLinearConstTask",
    data: {
      a, b, c, d, k, latex,
      x0_num: x0.num, x0_den: x0.den, x0_val: x0.num / x0.den,
      xr_num: xr.num, xr_den: xr.den, xr_val: xr.num / xr.den,
      hasSolution: !noSolution,
    },
  };
}

/* ===== task ===== */
export function renderTask({ data }) {
  return (
    <p>Podaj dziedzinę i rozwiąż równanie: <InlineMath math={data.latex} /></p>
  );
}

/* ===== input ===== */
export function renderInput(_, userInput = {}, onChange, disabled) {
  const frac = userInput?.x; // może być string (stary), albo obiekt { raw, parsed }
  const hasRaw = typeof frac === "object" && frac?.raw;
  const preview = hasRaw ? formatFracPreview(frac.raw, "x") : null;

  return (
    <div className="flex flex-col gap-4">
      {/* Dziedzina */}
      <IntervalInput
        value={userInput?.D || ""}
        onChange={(val) => onChange({ ...userInput, D: val })}
        disabled={disabled}
        asString
        previewWithX
        prefixAbove
        placeholder="np. ℝ\{3}  lub  ℝ\{3, 5}  lub  ℝ - {3}"
      />

      {/* Rozwiązanie */}
      <div className="flex flex-col gap-1">
        <div style={{ lineHeight: 1 }}><InlineMath math={"x\\;="} /></div>
        <FractionInput
          value={hasRaw ? frac : (typeof frac === "string" ? frac : "")}
          onChange={(val) => onChange({ ...userInput, x: val })}
          disabled={disabled}
          placeholder='np. 2, 3/5, 1 2/3 lub brak'
          label="x"
        />
        {preview ? (
          <div style={{ pointerEvents: "none" }}>
            <strong>Podgląd:</strong> <InlineMath math={preview} />
          </div>
        ) : null}
      </div>
    </div>
  );
}

// ========= helpers: "brak rozwiązań" (ujednolicone) =========
function normalizeUserNumberText(txt) {
  return String(txt ?? "")
    .replace(/[−–—]/g, "-") // różne myślniki → zwykły minus
    .replace(/\u00A0|\u202F|\u2009|\u200A|\u200B/g, " ") // wąskie spacje itp.
    .replace(/\s+/g, " ")
    .trim();
}

function matchesNoneKeyword(s) {
  if (!s) return false;
  const t = normalizeUserNumberText(s).toLowerCase();

  // zdejmujemy polskie znaki na potrzeby dopasowania słów-kluczy
  const noDia = t
    .replace(/ą/g, "a").replace(/ć/g, "c").replace(/ę/g, "e")
    .replace(/ł/g, "l").replace(/ń/g, "n").replace(/ó/g, "o")
    .replace(/ś/g, "s").replace(/ż/g, "z").replace(/ź/g, "z");

  return (
    t === "∅" ||
    t === "ø" || t === "Ø" ||
    noDia === "brak" ||
    noDia === "brak odpowiedzi" ||
    noDia === "brak rozwiazan" ||
    noDia === "pusty" ||
    noDia === "zbior pusty" ||
    t === "empty" ||
    t === "none" ||
    /^\{\s*\}$/.test(t)
  );
}


export function checkAnswer({ data }, userInput = {}) {
  // dziedzina
  const rawD = typeof userInput?.D === "string" ? userInput.D : (userInput?.D?.raw || "");
  const parsedD = parseIntervalInput(rawD);
  const x0 = data.x0_val;
  const eqTol = (x, y) => Math.abs(x - y) <= 0.01 + 1e-9;

  let domainOK = false;
  if (parsedD) {
    if (parsedD.kind === "complement") {
      domainOK = eqTol(parsedD.point, x0);
    } else if (parsedD.kind === "intervals") {
      const ints = parsedD.intervals;
      if (ints.length === 2) {
        const [I, J] = ints;
        const ok1 =
          I.left === -Infinity && !I.rightClosed && eqTol(I.right, x0) &&
          eqTol(J.left, x0) && !J.leftClosed && J.right === Infinity;
        const ok2 =
          J.left === -Infinity && !J.rightClosed && eqTol(J.right, x0) &&
          eqTol(I.left, x0) && !I.leftClosed && I.right === Infinity;
        domainOK = ok1 || ok2;
      }
    }
  }

  // rozwiązanie
  const hasSolution = data.hasSolution;
  const frac = userInput?.x;

  // --- "brak rozwiązań" ---
  const rawString =
    typeof frac === "string"
      ? frac
      : (typeof frac === "object" ? frac.raw : "");

  const wantNoneKeyword = matchesNoneKeyword(rawString);

  const isEmptyObj = typeof frac === "object" && !frac?.raw;
  const isEmptyStr =
    typeof frac === "string" &&
    normalizeUserNumberText(rawString) === "";

  const wantNone =
    wantNoneKeyword || (!hasSolution && (isEmptyObj || isEmptyStr));

  // --- liczba z FractionInput lub tekstu (legacy) ---
  let parsedX = NaN;
  if (typeof frac === "object" && Number.isFinite(frac?.parsed)) {
    parsedX = frac.parsed;
  } else if (typeof frac === "string") {
    parsedX = parseRootFractionInput(
      normalizeUserNumberText(rawString).replace(",", ".")
    );
  }

  const xr = data.xr_val;

  let solutionOK = false;
  if (!hasSolution) {
    solutionOK = wantNone;
  } else {
    if (Number.isFinite(parsedX)) solutionOK = eqTol(parsedX, xr);
  }

  return {
    correct: domainOK && solutionOK,
    expected: { D: x0, x: hasSolution ? xr : null, hasSolution },
  };
}


/* ===== short answer ===== */
export function renderAnswer({ data }) {
  const x0L = finalPointLatex(data.x0_num, data.x0_den);
  const sol = data.hasSolution ? finalPointLatex(data.xr_num, data.xr_den) : null;

  return (
    <div style={{ textAlign: "left", marginTop: "0.5rem" }}>
      <div><InlineMath math={`x\\in\\;\\mathbb{R}\\setminus\\{${x0L}\\}`} /></div>
      <div style={{ marginTop: "0.4rem" }}>
        {sol ? <InlineMath math={`x\\;=\\;${sol}`} /> : <i>Brak rozwiązań (wynik wypada z dziedziny).</i>}
      </div>
    </div>
  );
}

/* ===== user answer preview ===== */
export function renderUserAnswer(userInput = {}) {
  const rawD = typeof userInput === "string" ? userInput : (userInput?.D?.raw || userInput?.D || "");
  const dLatex = intervalToLatex(rawD, { withX: true });

  // FractionInput: jeśli mamy obiekt z raw, zrób ładny preview; w legacy-string użyj formatera „num”
  const xObj = userInput?.x;
  const xLatex =
    (typeof xObj === "object" && xObj?.raw)
      ? formatFracPreview(xObj.raw, "x")
      : (typeof xObj === "string"
          ? (formatNumLatex(String(xObj), "") || String(xObj)).trim()
          : "");

  return (
    <div className="space-y-1">
      {rawD ? <div><InlineMath math={`\\displaystyle ${dLatex}`} /></div> : null}
      {xLatex ? <div><InlineMath math={xLatex} /></div> : null}
      {!rawD && !xLatex ? <i>(brak odpowiedzi)</i> : null}
    </div>
  );
}

export function renderExplanation({ data }) {
  const { a, b, c, d, k, x0_num, x0_den, xr_num, xr_den, hasSolution } = data;

  const aL = toLatexNumber(a),
        bL = toLatexNumber(b),
        cL = toLatexNumber(c),
        dL = toLatexNumber(d),
        kL = toLatexNumber(k);

  /* Krok 1: warunek z mianownika */
  const opSubD = d < 0 ? `+\\,${toLatexNumber(-d)}` : `-\\,${dL}`;
  const opDivC = c < 0 ? `:\\,(${cL})` : `:\\,${cL}`;

  /* Krok 2: poprawne „przenosiny” po kresce */
  const kc = k * c;
  const opMoveX =
    kc < 0 ? `+\\,${coeffX(-kc)}` : `-\\,${coeffX(kc)}`; // np. -30x → | +30x,  24x → | -24x
  const opMoveB =
    b  < 0 ? `+\\,${toLatexNumber(-b)}` : `-\\,${bL}`;   // np.  -2  → | +2,    7 → | -7

  const A   = a - k * c;               // współczynnik przy x po zebraniu
  const rhs = k * d - b;

  // adnotacja „dzielimy przez A”: ukryj, gdy |A|=1; nawiasy tylko dla A<0
  const opDivA =
    Math.abs(A) === 1 ? "" : (A < 0 ? `:\\,(${toLatexNumber(A)})` : `:\\,${toLatexNumber(A)}`);
  const opDivACols = opDivA ? ` &\\Big|&\\ ${opDivA}` : "";

  const x0Final = finalPointLatex(x0_num, x0_den);
  const xrFinal = finalPointLatex(xr_num, xr_den); // jedna forma (mieszana jeśli ma sens)

  return (
    <div className="space-y-4">
      {/* Krok 1: warunek + wykluczenie */}
      <p><strong>Krok 1:</strong> Warunek z mianownika i wykluczenie punktu.</p>
      <div style={{ textAlign: "center" }}>
        <BlockMath math={String.raw`\begin{aligned}
          ${coeffX(c)} ${withSign(d)} &\neq 0   &\big|&\; ${opSubD} \\
          ${coeffX(c)} &\neq ${toLatexNumber(-d)} &\big|&\; ${opDivC}
        \end{aligned}`} />
      </div>
      <div style={{ textAlign: "center" }}>
        <BlockMath math={`x \\neq ${x0Final}`} />
      </div>

      {/* Krok 2: mnożymy przez mianownik i rozwiązujemy */}
      <p><strong>Krok 2:</strong> Mnożymy obustronnie przez <InlineMath math={"(cx+d)"} /> (bo <InlineMath math={"cx+d\\neq 0"} />) i rozwiązujemy.</p>
      <div style={{ textAlign: "center" }}>
        <BlockMath math={
          String.raw`\begin{aligned}
            \frac{${aL}x ${withSign(b).replace(" ", "")}}{{${coeffX(c)} ${withSign(d).replace(" ", "")}}}
              &= ${kL} &\Big|&\ \cdot\,(${coeffX(c)} ${withSign(d)}) \\
            ${aL}x ${withSign(b)} &= ${kL}\big(${coeffX(c)} ${withSign(d)}\big) \\
            ${aL}x ${withSign(b)} &= ${coeffX(kc)} ${withSign(k*d)} &\Big|&\ ${opMoveX} \\
            ${coeffX(A)} ${withSign(b)} &= ${toLatexNumber(k*d)} &\Big|&\ ${opMoveB} \\
            ${coeffX(A)} &= ${toLatexNumber(rhs)}${opDivACols} \\
            x &= ${xrFinal}
          \end{aligned}`
        } />
      </div>

      {/* Krok 3: odpowiedź + sprawdzenie dziedziny */}
      <p>
        <strong>Krok 3:</strong> Odpowiedź — sprawdzamy, czy <InlineMath math={"x"} /> należy do
        dziedziny (musi być <InlineMath math={`x\\neq ${x0Final}`} />).
      </p>
      <div style={{ textAlign: "center", marginBottom: "0.25rem" }}>
        <BlockMath math={`\\mathbb{R}\\setminus\\{${x0Final}\\}`} />
      </div>
      <div style={{ textAlign: "center", marginTop: "-2px" }}>
        {hasSolution ? (
          <em>Należy do dziedziny, więc jest rozwiązaniem.</em>
        ) : (
          <em>Nie należy do dziedziny, więc rozwiązań brak.</em>
        )}
      </div>
      {hasSolution ? (
        <div style={{ textAlign: "center" }}>
          <BlockMath math={`x = ${xrFinal}`} />
        </div>
      ) : (
        <div style={{ textAlign: "center" }}>
          <em>Brak rozwiązań, bo wynik wypada z dziedziny.</em>
        </div>
      )}
    </div>
  );
}
